import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import Mousetrap from 'mousetrap';

import localUtil from '../../lib/util';

// Red = super-admin only (hasToBeAdmin). Amber = unlocked by a role (isRoleLink: support staff,
// club admin, range officer, GPS admin...). Everything else uses the normal text colour.
function linkIconClass(link) {
    if (link.hasToBeAdmin) return 'text-danger';
    if (link.isRoleLink) return 'text-warning';
    return 'text-secondary';
}

function linkTextClass(link, darkmode) {
    if (link.hasToBeAdmin) return 'text-danger font-weight-lighter font-style-narrow';
    if (link.isRoleLink) return 'text-warning font-weight-lighter font-style-narrow';
    return darkmode ? 'text-white' : 'text-dark';
}

@observer
class Menu extends Component {
	constructor(props) {
        super(props);
        this.state = {
            showHelp: false,
            selectedIndex: 0,
            searchQuery: '',
            shortcutBuffer: '',
            hasNavigated: false,
        };
        this.containerRef = null;
        this.shortcutTimeout = null;
    }

    getFilteredItems = () => {
        const { menus } = this.props;
        const { searchQuery } = this.state;
        const { appState, userStore } = this.props.stores;
        const { isCordova, opts = {}, showAdminFields } = appState;
        const { isAdmin } = userStore;

        const items = [];
        menus.forEach((area, areaIdx) => {
            area.links.forEach((link, linkIdx) => {
                // Apply visibility filters
                if (link.hasToHaveCordova && !isCordova) return;
                if (!showAdminFields && link.hasToBeAdmin) return;
                if (link.hasToBeAdmin && !isAdmin) return;
                if (link.type === 'inspections' && !opts.showInspections) return;

                // Apply search filter
                if (searchQuery) {
                    const searchText = link.searchText || '';
                    if (!searchText.toLowerCase().includes(searchQuery.toLowerCase())) {
                        return;
                    }
                }

                items.push({ link, areaIdx, linkIdx });
            });
        });
        return items;
    }

    executeShortcut = (shortcut) => {
        const { menus } = this.props;
        // Find matching shortcut in menu items
        for (const area of menus) {
            for (const link of area.links) {
                if (link.keyShortCut === shortcut) {
                    if (link.func) {
                        link.func();
                    } else if (link.href) {
                        route(link.href);
                        this.toggleDrawer();
                    }
                    return true;
                }
            }
        }
        return false;
    }

    handleKeyUp = (e) => {
        // Hide shortcuts overlay when 'j' is released
        if (e.key.toLowerCase() === 'j') {
            this.setState({ showHelp: false });
        }
    }

    handleKeyDown = (e) => {
        const { searchQuery, selectedIndex, shortcutBuffer } = this.state;
        const filteredItems = this.getFilteredItems();
        const maxIndex = filteredItems.length - 1;

        // If Shift is held, handle shortcuts directly
        if (e.shiftKey && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            e.stopPropagation();

            const key = e.key.toLowerCase();

            // Special case: 'j' shows keyboard shortcuts overlay
            if (key === 'j' && !shortcutBuffer) {
                this.setState({ showHelp: true });
                return;
            }

            // Clear any existing timeout
            if (this.shortcutTimeout) {
                clearTimeout(this.shortcutTimeout);
            }

            // Build the shortcut sequence
            const newBuffer = shortcutBuffer ? `${shortcutBuffer} ${key}` : key;

            // Check if this matches a shortcut
            if (this.executeShortcut(newBuffer)) {
                this.setState({ shortcutBuffer: '' });
                return;
            }

            // Check if any shortcut starts with this buffer
            const { menus } = this.props;
            let hasPartialMatch = false;
            for (const area of menus) {
                for (const link of area.links) {
                    if (link.keyShortCut && link.keyShortCut.startsWith(newBuffer)) {
                        hasPartialMatch = true;
                        break;
                    }
                }
                if (hasPartialMatch) break;
            }

            if (hasPartialMatch) {
                // Wait for more keys
                this.setState({ shortcutBuffer: newBuffer });
                // Clear buffer after 1 second of no input
                this.shortcutTimeout = setTimeout(() => {
                    this.setState({ shortcutBuffer: '' });
                }, 1000);
            } else {
                // No match, clear buffer
                this.setState({ shortcutBuffer: '' });
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                this.setState({
                    selectedIndex: selectedIndex < maxIndex ? selectedIndex + 1 : 0,
                    hasNavigated: true,
                });
                break;

            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                this.setState({
                    selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : maxIndex,
                    hasNavigated: true,
                });
                break;

            case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                if (filteredItems.length > 0 && filteredItems[selectedIndex]) {
                    const { link } = filteredItems[selectedIndex];
                    if (link.func) {
                        link.func();
                    } else if (link.href) {
                        route(link.href);
                        this.toggleDrawer();
                    }
                }
                break;

            case 'Escape':
                if (searchQuery) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.setState({ searchQuery: '', selectedIndex: 0 });
                }
                // If no search query, let the drawer handle the close
                break;

            case 'Backspace':
                if (searchQuery) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.setState({
                        searchQuery: searchQuery.slice(0, -1),
                        selectedIndex: 0,
                    });
                }
                break;

            default:
                // Handle printable characters for search (only if Shift is not held)
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.setState({
                        searchQuery: searchQuery + e.key,
                        selectedIndex: 0,
                    });
                }
                break;
        }
    }

    toggleDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        appState.toggleDrawer(false, drawerLevel);
    }

    showHelp = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showHelp } = this.state;
        if (!showHelp) {
            this.setState({ showHelp: true });
        }
    }

    hideHelp = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showHelp } = this.state;
        if (showHelp) {
            this.setState({ showHelp: false });
        }
    }

    componentDidMount() {
        const { menus } = this.props;
        Mousetrap.bind('j', this.showHelp, 'keydown');
        Mousetrap.bind('j', this.hideHelp, 'keyup');
        menus.forEach(mp => {
            mp.links.forEach(link => {
                if (link.keyShortCut) {
                    Mousetrap.bind([link.keyShortCut], () => {
                        if (link.func) {
                            link.func();
                        } else {
                            route(link.href);
                            this.toggleDrawer();
                        }
                    });
                }
            });
        });

        // Auto-focus the container for keyboard navigation
        if (this.containerRef) {
            this.containerRef.focus({ preventScroll: true });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Scroll selected item into view only after user has navigated with keyboard
        if (this.state.hasNavigated && prevState.selectedIndex !== this.state.selectedIndex) {
            const el = this.containerRef?.querySelector('.menu-item-highlighted');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }

    componentWillUnmount() {
        const { menus } = this.props;
        Mousetrap.unbind('j');
        // Unbind shortcuts
        menus.forEach(mp => {
            mp.links.forEach(link => {
                if (link.keyShortCut) {
                    Mousetrap.unbind([link.keyShortCut]);
                }
            });
        });
        // Clear any pending timeout
        if (this.shortcutTimeout) {
            clearTimeout(this.shortcutTimeout);
        }
    }

    render() {
        const { showHelp, searchQuery, selectedIndex, shortcutBuffer } = this.state;
        const { menus } = this.props;
        const { appState, userStore } = this.props.stores;
        const { isCordova, opts = {}, showAdminFields } = appState;
        const { user, isAdmin, saved } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // Get filtered items for tracking the flat index
        const filteredItems = this.getFilteredItems();

        // Build a set for quick lookup of which items are visible
        const visibleItemKeys = new Set();
        filteredItems.forEach(({ areaIdx, linkIdx }) => {
            visibleItemKeys.add(`${areaIdx}-${linkIdx}`);
        });

        // Track flat index while rendering
        let flatIndex = -1;

        return (
            <div
                ref={c => this.containerRef = c}
                tabIndex={0}
                onKeyDown={this.handleKeyDown}
                onKeyUp={this.handleKeyUp}
                style='outline: none;'
            >
                {/* Shortcut indicator when typing shortcuts with Shift */}
                {shortcutBuffer && (
                    <div
                        class='menu-shortcut-indicator'
                        style={`
                            position: sticky;
                            top: 0;
                            background: ${darkmode ? 'rgba(80, 80, 0, 0.9)' : 'rgba(255, 193, 7, 0.9)'};
                            color: ${darkmode ? 'white' : 'black'};
                            padding: 8px 16px;
                            border-radius: 10px;
                            margin: 0 16px 8px;
                            z-index: 1;
                            display: flex;
                            align-items: center;
                        `}
                    >
                        <i class='fa-solid fa-keyboard me-2' />
                        Shortcut: {shortcutBuffer}
                    </div>
                )}

                {/* Search indicator when filtering */}
                {searchQuery && (
                    <div
                        class='menu-search-indicator'
                        style={`
                            position: sticky;
                            top: 0;
                            background: ${darkmode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 123, 255, 0.9)'};
                            color: white;
                            padding: 8px 16px;
                            border-radius: 10px;
                            margin: 0 16px 8px;
                            z-index: 1;
                            display: flex;
                            align-items: center;
                        `}
                    >
                        <i class='fa-solid fa-search me-2' />
                        {searchQuery}
                        {filteredItems.length === 0 && (
                            <span class='ms-2 text-white-50'>- no matches</span>
                        )}
                    </div>
                )}

                {menus && menus.map((area, areaIdx) => {
                    // Check if any links in this area are visible
                    const hasVisibleLinks = area.links.some((link, linkIdx) => visibleItemKeys.has(`${areaIdx}-${linkIdx}`));
                    if (!hasVisibleLinks) {
                        return '';
                    }

                    return (<>
                        <div class='mx-4 font-weight-lighter px-3 py-1 text-muted' >
                            <small class='text-uppercase'>
                                {area.name}
                            </small>
                        </div>
                        <div
                            class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-4 d-flex flex-column`}
                            style={`
                                font-size: 18px !important;
                                border-radius: 10px;
                            `}
                        >
                            {area.links && area.links.map((link, linkIdx) => {
                                const itemKey = `${areaIdx}-${linkIdx}`;
                                if (!visibleItemKeys.has(itemKey)) {
                                    return '';
                                }

                                // Increment flat index for visible items
                                flatIndex++;
                                const isHighlighted = flatIndex === selectedIndex;

                                // Find the rendered position for border styling
                                const visibleIndexInArea = area.links.slice(0, linkIdx).filter((l, i) => visibleItemKeys.has(`${areaIdx}-${i}`)).length;

                                return (<>
                                    <div
                                        class={`d-flex justify-content-center align-items-center position-relative ps-2 pe-3 ${isHighlighted ? 'menu-item-highlighted' : ''}`}
                                        style={`
                                            ${visibleIndexInArea > 0 ? `border-top: 1px ${darkmode ? '#303030' : '#f0f0f0'} solid;` : ''}
                                            ${isHighlighted ? `background-color: ${darkmode ? 'rgba(0, 123, 255, 0.3)' : 'rgba(0, 123, 255, 0.15)'}; outline: 2px solid #007bff; outline-offset: -2px; border-radius: 4px;` : ''}
                                        `}
                                    >
                                        <div class='d-flex justify-content-center align-items-center' style='width: 44px; height: 44px;'>
                                            <i class={`${link.icon} ${linkIconClass(link)}`} />
                                        </div>
                                        <span class={`flex-grow-1`} style='line-height: 1.3em;'>
                                            {link.func ? <>
                                                <button
                                                    onClick={link.func}
                                                    class={`${linkTextClass(link, darkmode)} btn btn-link btn-block text-start p-0 d-flex justify-content-between align-items-center`}
                                                    style='font-size: 18px !important;'
                                                >
                                                    <span>{link.name}</span>
                                                    {/* <i class='fa-solid fa-angle-right float-right' style='font-size: 0.9em; color: #e0e0e0;' /> */}
                                                </button>
                                            </> : <>
                                                <a
                                                    href={link.href}
                                                    class={`${linkTextClass(link, darkmode)} stretched-link`}
                                                    onClick={this.toggleDrawer}
                                                >
                                                    {link.name}
                                                </a>
                                            </>}
                                            {link.keyShortCut && showHelp ? <span class='keyboardShortcut'>[{link.keyShortCut}]</span> : ''}
                                        </span>
                                    </div>
                                </>);
                            })}
                        </div>
                        <div class='mx-4 font-weight-lighter px-3 pb-1 text-muted' >
                            <small class=''>
                                {area.help}
                            </small>
                        </div>
                    </>);
                })}
            </div>
        );
    }
}

export default Menu;
